import { CreditCard, Filter, RefreshCw } from "lucide-react";
import { useState } from "react";

import { ApiError } from "@/api/client";
import { useAllBillingEvents } from "@/api/hooks";
import type { BillingEventResponse } from "@/api/types";
import { formatCents } from "@/lib/format";

/** Stripe webhook event log viewer. Reads
 *  ``/admin/billing-events`` (cross-org, newest first). Powers
 *  billing-issue triage: find the failed invoice, the
 *  ``customer.subscription.deleted`` that confused you, the dispute
 *  from earlier today.
 *
 *  Failed events get red highlighting. ``succeeded=false`` filter
 *  surfaces only those for fast triage. */
export function BillingEventsPage() {
  const [eventTypeFilter, setEventTypeFilter] = useState("");
  const [succeededFilter, setSucceededFilter] = useState<"all" | "ok" | "failed">("all");
  const [limit, setLimit] = useState(100);

  const events = useAllBillingEvents({
    event_type: eventTypeFilter || undefined,
    succeeded: succeededFilter === "all" ? undefined : succeededFilter === "ok",
    limit,
  });

  return (
    <div className="space-y-5">
      <div>
        <h1 className="font-mono text-2xl font-bold tracking-tight">Billing events</h1>
        <p className="mt-1 max-w-3xl text-sm text-graphite-soft">
          Stripe webhook events + admin-initiated invoice actions across all orgs. Newest first.
          Failed events get a red row — those are the ones a customer's about to call about.
        </p>
      </div>

      <div className="flex flex-wrap items-end gap-3 rounded-lg border border-rule bg-paper-warm p-4">
        <div className="flex-1 min-w-[180px]">
          <span className="label-mono mb-1 block">Event type contains</span>
          <div className="relative">
            <Filter
              className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-graphite-soft"
              aria-hidden
            />
            <input
              type="text"
              value={eventTypeFilter}
              onChange={(e) => setEventTypeFilter(e.target.value)}
              placeholder="invoice.payment_failed, dispute, …"
              className="w-full rounded border border-rule bg-paper py-1.5 pl-8 pr-2 font-mono text-sm focus:border-graphite focus:outline-none"
            />
          </div>
        </div>
        <div className="w-32">
          <span className="label-mono mb-1 block">Outcome</span>
          <select
            value={succeededFilter}
            onChange={(e) => setSucceededFilter(e.target.value as "all" | "ok" | "failed")}
            className="w-full rounded border border-rule bg-paper py-1.5 px-2 font-mono text-sm focus:border-graphite focus:outline-none"
          >
            <option value="all">All</option>
            <option value="ok">Succeeded</option>
            <option value="failed">Failed</option>
          </select>
        </div>
        <div className="w-24">
          <span className="label-mono mb-1 block">Limit</span>
          <select
            value={limit}
            onChange={(e) => setLimit(Number(e.target.value))}
            className="w-full rounded border border-rule bg-paper py-1.5 px-2 font-mono text-sm focus:border-graphite focus:outline-none"
          >
            <option value={50}>50</option>
            <option value={100}>100</option>
            <option value={250}>250</option>
            <option value={500}>500</option>
          </select>
        </div>
        <button
          type="button"
          onClick={() => events.refetch()}
          disabled={events.isFetching}
          className="inline-flex items-center gap-1.5 rounded border border-rule bg-paper px-3 py-1.5 font-mono text-xs font-semibold text-graphite transition hover:bg-pencil-yellow-soft disabled:opacity-40"
        >
          <RefreshCw
            className={`h-3.5 w-3.5 ${events.isFetching ? "animate-spin" : ""}`}
            aria-hidden
          />
          Refresh
        </button>
      </div>

      {events.isLoading && <p className="text-sm text-graphite-soft">Loading events…</p>}
      {events.error && (
        <p className="rounded border border-red-pencil/30 bg-red-pencil/5 px-3 py-2 text-sm text-red-pencil">
          {events.error instanceof ApiError ? events.error.detail : "Could not load events."}
        </p>
      )}
      {events.data && events.data.length === 0 && (
        <p className="rounded-lg border border-rule bg-paper-warm p-5 text-sm text-graphite-soft">
          No events match this filter.
        </p>
      )}

      {events.data && events.data.length > 0 && (
        <div className="rounded-lg border border-rule bg-paper-warm">
          <div className="border-b border-rule px-4 py-2 text-xs text-graphite-soft">
            <span className="label-mono">{events.data.length} events</span>
          </div>
          <ul>
            {events.data.map((evt) => (
              <BillingEventRow key={evt.id} event={evt} />
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function BillingEventRow({ event }: { event: BillingEventResponse }) {
  return (
    <li
      className={`border-b border-rule/60 px-4 py-3 last:border-b-0 ${event.succeeded ? "" : "bg-red-pencil/5"}`}
    >
      <div className="flex items-start gap-3">
        <CreditCard
          className={`mt-0.5 h-4 w-4 flex-none ${event.succeeded ? "text-graphite-soft" : "text-red-pencil"}`}
          aria-hidden
        />
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
            <span
              className={`font-mono text-sm font-semibold tabular-nums ${event.succeeded ? "" : "text-red-pencil"}`}
            >
              {event.event_type}
            </span>
            <span
              className={`rounded px-1.5 py-0.5 font-mono text-[10px] uppercase tracking-wider ${event.stripe_mode === "live" ? "bg-graphite text-paper" : "bg-pencil-yellow-soft text-graphite"}`}
            >
              {event.stripe_mode}
            </span>
            <span className="font-mono text-xs text-graphite-soft tabular-nums">
              {new Date(event.created_at).toLocaleString()}
            </span>
            {event.amount_cents != null && (
              <span className="ml-auto font-mono text-sm font-semibold tabular-nums">
                {formatCents(event.amount_cents)}
              </span>
            )}
          </div>
          <div className="mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-0.5 font-mono text-xs text-graphite-soft">
            {event.organization_id ? (
              <span>org {event.organization_id.slice(0, 8)}…</span>
            ) : (
              <span className="text-graphite-faint">unmapped org</span>
            )}
            {event.invoice_id && <span>inv {event.invoice_id.slice(0, 14)}…</span>}
            {event.stripe_event_id && <span>evt {event.stripe_event_id.slice(0, 14)}…</span>}
          </div>
          {event.error_message && (
            <p className="mt-1 rounded border border-red-pencil/30 bg-red-pencil/5 px-2 py-1 text-xs text-red-pencil">
              {event.error_message}
            </p>
          )}
        </div>
      </div>
    </li>
  );
}
